import type { GeometryKey } from '../locales/keys';

export type AreaShape =
  | 'parallelogram'
  | 'triangle'
  | 'trapezoid'
  | 'circle'
  | 'regular-polygon'
  | 'composite'
  | 'heron';

export interface AreaPreset {
  id: string;
  shape: AreaShape;
  labelKey: GeometryKey;
  advancedOnly: boolean;
  // Parallelogram / triangle / trapezoid config
  base?: number;
  height?: number;
  shear?: number;
  // Trapezoid top side
  topBase?: number;
  // Circle config
  radius?: number;
  sectors?: number;
  // Regular polygon config
  polygonSides?: number;
  circumradius?: number;
  // Heron triangle: side lengths
  sideA?: number;
  sideB?: number;
  sideC?: number;
}

export const AREA_PRESETS: readonly AreaPreset[] = [
  // Foundation presets — one per shape
  {
    id: 'parallelogram-3x4',
    shape: 'parallelogram',
    labelKey: 'area.preset.parallelogram',
    advancedOnly: false,
    base: 6,
    height: 4,
    shear: 2,
  },
  {
    id: 'triangle-6x4',
    shape: 'triangle',
    labelKey: 'area.preset.triangle',
    advancedOnly: false,
    base: 6,
    height: 4,
    shear: 0,
  },
  {
    id: 'trapezoid-3-5-4',
    shape: 'trapezoid',
    labelKey: 'area.preset.trapezoid',
    advancedOnly: false,
    base: 5,
    topBase: 3,
    height: 4,
  },
  {
    id: 'circle-r4',
    shape: 'circle',
    labelKey: 'area.preset.circle',
    advancedOnly: false,
    radius: 4,
    sectors: 16,
  },
  // Advanced presets
  {
    id: 'hexagon-apothem',
    shape: 'regular-polygon',
    labelKey: 'area.preset.hexagon-apothem',
    advancedOnly: true,
    polygonSides: 6,
    circumradius: 4,
  },
  {
    id: 'l-shape',
    shape: 'composite',
    labelKey: 'area.preset.l-shape',
    advancedOnly: true,
  },
  {
    id: 'heron-check',
    shape: 'heron',
    labelKey: 'area.preset.heron-check',
    advancedOnly: true,
    sideA: 5,
    sideB: 6,
    sideC: 7,
  },
];

export function isValidAreaPreset(p: AreaPreset): boolean {
  if (typeof p.id !== 'string' || p.id.length === 0) return false;
  const validShapes: AreaShape[] = [
    'parallelogram',
    'triangle',
    'trapezoid',
    'circle',
    'regular-polygon',
    'composite',
    'heron',
  ];
  if (!validShapes.includes(p.shape)) return false;
  if (typeof p.labelKey !== 'string' || p.labelKey.length === 0) return false;
  if (typeof p.advancedOnly !== 'boolean') return false;
  if (p.base !== undefined && typeof p.base !== 'number') return false;
  if (p.height !== undefined && typeof p.height !== 'number') return false;
  if (p.shear !== undefined && typeof p.shear !== 'number') return false;
  if (p.topBase !== undefined && typeof p.topBase !== 'number') return false;
  if (p.radius !== undefined && typeof p.radius !== 'number') return false;
  if (p.sectors !== undefined) {
    if (typeof p.sectors !== 'number' || p.sectors < 4) return false;
  }
  if (p.polygonSides !== undefined) {
    if (typeof p.polygonSides !== 'number' || p.polygonSides < 3) return false;
  }
  if (p.circumradius !== undefined && typeof p.circumradius !== 'number') return false;
  if (p.sideA !== undefined && typeof p.sideA !== 'number') return false;
  if (p.sideB !== undefined && typeof p.sideB !== 'number') return false;
  if (p.sideC !== undefined && typeof p.sideC !== 'number') return false;
  return true;
}

export function validateAreaPresetIds(presets: readonly AreaPreset[]): boolean {
  const ids = new Set<string>();
  for (const preset of presets) {
    if (ids.has(preset.id)) return false;
    ids.add(preset.id);
  }
  return true;
}

/** Foundation shapes expected — one preset each. */
const FOUNDATION_SHAPES: AreaShape[] = ['parallelogram', 'triangle', 'trapezoid', 'circle'];

export function validateFoundationCoverage(presets: readonly AreaPreset[]): boolean {
  const foundationIds = new Set(presets.filter((p) => !p.advancedOnly).map((p) => p.shape));
  return FOUNDATION_SHAPES.every((s) => foundationIds.has(s));
}
